import React, {createContext, useState, useCallback, useMemo, useContext} from 'react';
import {reportService} from '../services/reportService';

export const ReportContext = createContext();

export const ReportProvider = props => {
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState();
  const [err, setErr] = useState();
  const [customer, setCustomer] = useState();

  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortState, setSortState] = useState([{fieldName: 'ID', dir: 'desc'}]);
  const [reportTemplates, setReportTemplates] = useState([]);
  const [selectedReportTemplate, setSelectedReportTemplate] = useState(1);
  const [selectedReportTemplateName, setSelectedReportTemplateName] = useState('');
  const [selectedReportTemplateIsApiOnly, setSelectedReportTemplateIsApiOnly] = useState(false);
  const [reportTemplate, setReportTemplate] = useState([]);
  const [reportMetric, setReportMetric] = useState([]);
  const [functionList, setFunctionList] = useState([]);
  const [pointList, setPointList] = useState([]);
  const [functionTableList, setFunctionTableList] = useState([]);

  const [energyPoints, setEnergyPoints] = useState([]);
  const [energyPointPages, setEnergyPointPages] = useState(1);
  const [variablePoints, setVariablePoints] = useState([]);
  const [variablePointPages, setVariablePointPages] = useState(1);
  const [positionPoints, setPositionPoints] = useState([]);
  const [positionPointPages, setPositionPointPages] = useState(1);
  const [binaryPoints, setBinaryPoints] = useState([]);
  const [binaryPointPages, setBinaryPointPages] = useState(1);
  const [numericPoints, setNumericPoints] = useState([]);
  const [numericPointPages, setNumericPointPages] = useState(1);

  const listReports = useCallback(
    async customerId => {
      const result = await reportService.getReports(
        pageSize,
        pageIndex,
        sortState[0]?.fieldName,
        sortState[0]?.dir,
        customerId
      );
      setReports(result.data);
      setTotal(result.total);
    },
    [sortState, pageSize, pageIndex]
  );

  const listEquipment = useCallback(
    async (pageSize, pageIndex, sortField, sortDirection, customerId) => {
      return await reportService.getEquipment(
        pageSize,
        pageIndex,
        sortField,
        sortDirection,
        customerId
      );
    },
    []
  );

    const listAosComponents = useCallback(async (rootComponentId) => {
            return await reportService.getAosComponents(rootComponentId);
        },[]
    );

    const getComponentChildren = useCallback(async (rootComponentId) => {
            return await reportService.getComponentChildren(rootComponentId);
        },[]
    );

    const componentsExport = useCallback(async (componentId) => {
            return reportService.componentsExport(componentId);
        },[]
    );

    const componentsImport = useCallback(async (file) => {
            return reportService.componentsImport(file);
        },[]
    );

  const createReport = useCallback(
    (
      reportName,
      reportYear,
      reportStartMonth,
      reportEndMonth,
      benchmarkYear,
      benchmarkStartMonth,
      benchmarkEndMonth,
      electricRate,
      gasRate,
      gasUnit,
      equipmentIds,
      customerId,
      templateId
    ) => {
      return reportService.createReport(
        reportName,
        reportYear,
        reportStartMonth + 1,
        reportEndMonth + 1,
        benchmarkYear,
        benchmarkStartMonth + 1,
        benchmarkEndMonth + 1,
        electricRate,
        gasRate,
        gasUnit,
        equipmentIds,
        customerId,
        templateId
      );
    },
    []
  );

  const getReportTemplate = useCallback(async id => {
    setSelectedReportTemplate(id);
    const {template, idealMetrics, benchmarkMetrics, pointMetrics, functions, points} =
      await reportService.getReportTemplate(id);
    if (template) {
      setSelectedReportTemplateName(template.name);
      setSelectedReportTemplateIsApiOnly(template.isApiOnly);
    }
    const fnList = functions.map(x => x.dropdownOption);
    const ptList = points.map(x => x.dropdownOption);
    setFunctionList(fnList);
    setPointList(ptList);
    setFunctionTableList(functions);
    setReportTemplate({idealMetrics, benchmarkMetrics, pointMetrics});
    return;
  }, []);

  const getReportTemplates = useCallback(async () => {
    const reportTemplates = await reportService.getReportTemplates();
    setReportTemplates(reportTemplates);
    return;
  }, []);

  const getReportMetric = useCallback(async (id, type) => {
    const reportMetric = await reportService.getReportMetric(id, type);
    setReportMetric(reportMetric.data);
    return;
  }, []);

  const getEnergyPoints = useCallback(async activePage => {
    const pageSize = 10;
    const pageIndex = activePage - 1;
    const sortBy = 'id';
    const orderBy = 'ASC';
    const pointType = 'EP';
    const data = await reportService.getGenericPoints(
      pageSize,
      pageIndex,
      sortBy,
      orderBy,
      pointType
    );
    setEnergyPoints(data.genericPoints);
    setEnergyPointPages(Math.ceil(data.total / pageSize));
    return;
  }, []);

  const getVariablePoints = useCallback(async activePage => {
    const pageSize = 10;
    const pageIndex = activePage - 1;
    const sortBy = 'id';
    const orderBy = 'ASC';
    const pointType = 'VP';
    const data = await reportService.getGenericPoints(
      pageSize,
      pageIndex,
      sortBy,
      orderBy,
      pointType
    );
    setVariablePoints(data.genericPoints);
    setVariablePointPages(Math.ceil(data.total / pageSize));
    return;
  }, []);

  const getPositionPoints = useCallback(async activePage => {
    const pageSize = 10;
    const pageIndex = activePage - 1;
    const sortBy = 'id';
    const orderBy = 'ASC';
    const pointType = 'PP';
    const data = await reportService.getGenericPoints(
      pageSize,
      pageIndex,
      sortBy,
      orderBy,
      pointType
    );
    setPositionPoints(data.genericPoints);
    setPositionPointPages(Math.ceil(data.total / pageSize));
    return;
  }, []);

  const getBinaryPoints = useCallback(async activePage => {
    const pageSize = 10;
    const pageIndex = activePage - 1;
    const sortBy = 'id';
    const orderBy = 'ASC';
    const pointType = 'BP';
    const data = await reportService.getGenericPoints(
      pageSize,
      pageIndex,
      sortBy,
      orderBy,
      pointType
    );
    setBinaryPoints(data.genericPoints);
    setBinaryPointPages(Math.ceil(data.total / pageSize));
    return;
  }, []);

  const getNumericPoints = useCallback(async activePage => {
    const pageSize = 10;
    const pageIndex = activePage - 1;
    const sortBy = 'id';
    const orderBy = 'ASC';
    const pointType = 'NP';
    const data = await reportService.getGenericPoints(
      pageSize,
      pageIndex,
      sortBy,
      orderBy,
      pointType
    );
    console.log({data});
    setNumericPoints(data.genericPoints);
    setNumericPointPages(Math.ceil(data.total / pageSize));
    return;
  }, []);

  const upsertReportTemplateMetric = useCallback(
    async formData => {
      await reportService.upsertReportTemplateMetric(formData);
      getReportTemplate(selectedReportTemplate);
      return;
    },
    [getReportTemplate, selectedReportTemplate]
  );

  const deleteReportTemplate = useCallback(
    async id => {
      await reportService.deleteReportTemplate(id);
      setSelectedReportTemplate(1);
      getReportTemplates();
      getReportTemplate(1);
      return;
    },
    [getReportTemplates, getReportTemplate, setSelectedReportTemplate]
  );

  const deleteReportTemplateMetric = useCallback(
    async (id, type) => {
      await reportService.deleteReportTemplateMetric(id, type);
      getReportTemplate(selectedReportTemplate);
      return;
    },
    [getReportTemplate, selectedReportTemplate]
  );

  const updateReportTemplateMetricIndex = useCallback(
    async (id, type, moveUp) => {
      await reportService.updateReportTemplateMetricIndex(id, type, moveUp);
      getReportTemplate(selectedReportTemplate);
      return;
    },
    [getReportTemplate, selectedReportTemplate]
  );

  const createNewReportTemplate = useCallback(
    async (name, isApiOnly) => {
      const newId = await reportService.createNewReportTemplate(name, isApiOnly);
      getReportTemplates().then(() => {
        setSelectedReportTemplate(newId);
      });
    },
    [getReportTemplates, setSelectedReportTemplate]
  );

  const updateReportTemplate = useCallback(
    async (id, name, isApiOnly) => {
      await reportService.updateReportTemplate(id, name, isApiOnly);
      getReportTemplates().then(() => {
        getReportTemplate(id);
      });
    },
    [getReportTemplates, getReportTemplate]
  );

    const createSyrxReport = useCallback(
        async (reportName, customerId, groupId, equipmentIds) => {
            const response = await reportService.createSyrxReport(reportName, customerId, groupId, equipmentIds);
            return response;
        },[]
    );

    const clearReportMetric = useCallback(() => {
        setReportMetric([]);
    }, [setReportMetric]);

  const values = useMemo(
    () => ({
      results,
      isLoading,
      err,
      customer,
      total,
      pageIndex,
      pageSize,
      sortState,
      reports,
      setTotal,
      setPageIndex,
      setPageSize,
      setSortState,
      setCustomer,
      listReports,
      createReport,
      setResults,
      setIsLoading,
      listEquipment,
      listAosComponents,
      getComponentChildren,
      componentsExport,
      componentsImport,
      setErr,
      reportTemplate,
      reportTemplates,
      reportMetric,
      functionList,
      pointList,
      getReportTemplate,
      getReportTemplates,
      getReportMetric,
      energyPoints,
      energyPointPages,
      getEnergyPoints,
      variablePoints,
      variablePointPages,
      getVariablePoints,
      positionPoints,
      positionPointPages,
      getPositionPoints,
      binaryPoints,
      binaryPointPages,
      getBinaryPoints,
      numericPoints,
      numericPointPages,
      getNumericPoints,
      upsertReportTemplateMetric,
      deleteReportTemplate,
      deleteReportTemplateMetric,
      updateReportTemplateMetricIndex,
      createNewReportTemplate,
      updateReportTemplate,
      createSyrxReport,
      selectedReportTemplate,
      setSelectedReportTemplate,
      selectedReportTemplateName,
      setSelectedReportTemplateName,
      selectedReportTemplateIsApiOnly,
      setSelectedReportTemplateIsApiOnly,
      functionTableList,
      clearReportMetric
    }),
    [
      reports,
      results,
      isLoading,
      err,
      customer,
      pageIndex,
      pageSize,
      sortState,
      total,
      setCustomer,
      createReport,
      listReports,
      listEquipment,
      listAosComponents,
      getComponentChildren,
      componentsExport,
      componentsImport,
      reportTemplate,
      reportTemplates,
      reportMetric,
      getReportTemplate,
      getReportTemplates,
      getReportMetric,
      functionList,
      pointList,
      energyPoints,
      energyPointPages,
      getEnergyPoints,
      variablePoints,
      variablePointPages,
      getVariablePoints,
      positionPoints,
      positionPointPages,
      getPositionPoints,
      binaryPoints,
      binaryPointPages,
      getBinaryPoints,
      numericPoints,
      numericPointPages,
      getNumericPoints,
      upsertReportTemplateMetric,
      deleteReportTemplate,
      deleteReportTemplateMetric,
      updateReportTemplateMetricIndex,
      createNewReportTemplate,
      updateReportTemplate,
      selectedReportTemplate,
      setSelectedReportTemplate,
      selectedReportTemplateName,
      setSelectedReportTemplateName,
      selectedReportTemplateIsApiOnly,
      setSelectedReportTemplateIsApiOnly,
      createSyrxReport,
      functionTableList,
      clearReportMetric
    ]
  );

  return <ReportContext.Provider value={values}>{props.children}</ReportContext.Provider>;
};

export const useReport = () => {
  const context = useContext(ReportContext);

  if (context === undefined) {
    throw new Error('useReport must be used within a UserProvider component.');
  }

  return context;
};
